import React from 'react';

export const RECOVERY_MARKER = 'recoveryAttempts';
export const LAST_CRASH_UPTIME_MARKER = 'recoveryLastCrashUptime';
const MAX_RECOVERY_RELOADS = 1;
// How much longer than the last crash the app has to stay up before the budget
// is handed back. On a boot that has never crashed this is the whole wait.
const STABLE_UPTIME_MS = 60000;

/**
 * Set on `window` for the life of one document: this boot crashed.
 *
 * Deliberately not `sessionStorage`, which is the wrong lifetime - it survives
 * the reload, and the next boot has to start clean. Deliberately not a marker
 * some effect resets at mount either: a crash in the first render reaches
 * `onError` before Boot's own effect has run, so a mount-time reset would wipe
 * precisely the case that matters. `window` is per-document by construction,
 * which is the exact scope wanted, and a reload supplies a fresh one.
 */
export const CRASHED_THIS_BOOT = '__fixablyRecoveryCrashed';

/**
 * A crash used to clear the device identifier before reloading, so any error in
 * the Firebase subtree silently un-paired the tablet and made it register itself
 * as a brand new terminal on the next login. The pairing is now left alone: one
 * reload is attempted to recover from a transient fault, and a crash that repeats
 * before the app has stayed up leaves the fallback on screen instead of looping.
 *
 * The boundary wraps the router as well as the Firebase subtree. It has to: the
 * fallback says the app could not start, and as a sibling of a still-mounted
 * <Main/> it rendered *above* a live screen - #root is 100vh and Main's Box is
 * height 100%, so the running UI was pushed down and clipped rather than
 * replaced. Covering the router also turns a crash anywhere in the app into the
 * same bounded recovery instead of React 17's blank unmounted root.
 *
 * Counting recoveries rather than timing them is what makes this bounded. An
 * elapsed-time cooldown only suppresses crashes that arrive close together, so a
 * fault that reliably takes longer than the window to surface reloads the tablet
 * forever, which on a kiosk is indistinguishable from the device being broken.
 *
 * The count still has to be handed back eventually, or one transient fault costs
 * the tablet its recovery for the life of the session. WHEN it is handed back is
 * the whole difficulty, and a fixed delay does not work: clearing the count after
 * 60s of uptime reintroduces the loop it was written to prevent, because a fault
 * that surfaces at 90s always finds a freshly cleared count. The fixed window
 * does not disappear, it just moves from the crash comparison into the reset.
 * See `recoveryResetDelayMs` for what replaces it.
 */
export function recoverFromCrash() {
    // Set before any early return, because the flag records the fact - this boot
    // crashed - rather than one branch's outcome. Only the suppressed-crash path
    // below can be observed acting on it today: the reload path replaces the
    // document, and the storage-unavailable path never arms a reset to begin
    // with. Defining it by the fact is what keeps that true if a path is added.
    window[CRASHED_THIS_BOOT] = true;

    let attempts = 0;
    try {
        attempts = Number(window.sessionStorage.getItem(RECOVERY_MARKER)) || 0;
    } catch (e) {
        // Storage can be unavailable (private mode, blocked site data). Without a
        // marker we cannot tell a first crash from a loop, so do not reload.
        return;
    }

    if (attempts >= MAX_RECOVERY_RELOADS) {
        return;
    }

    try {
        window.sessionStorage.setItem(RECOVERY_MARKER, String(attempts + 1));
        // How long this boot survived. The next boot has to beat it before the
        // count is handed back, which is what stops a slow fault looping.
        window.sessionStorage.setItem(LAST_CRASH_UPTIME_MARKER, String(uptimeMs()));
    } catch (e) {
        return;
    }

    window.location.reload();
}

/**
 * Milliseconds this document has been open.
 *
 * `performance.now()` rather than a `Date.now()` difference: it is monotonic, it
 * is already zeroed at navigation start, and it cannot be moved by a clock
 * correction on a tablet that has been asleep.
 */
function uptimeMs() {
    try {
        return Math.max(0, Math.round(window.performance.now()));
    } catch (e) {
        return 0;
    }
}

/**
 * How long this boot must last before the recovery count is handed back.
 *
 * The rule is "outlive the last crash, by a margin". A boot that has already run
 * longer than the previous one managed is evidence the fault is not reproducing,
 * which a fixed 60s says nothing about: a fault that always surfaces at 90s
 * cleared its count at 60s every single time and reloaded the kiosk forever,
 * never reaching the fallback that identifies the device.
 *
 * Exported because this is the property the whole design rests on, and the test
 * that claimed to pin it previously asserted nothing - it mocked `Date.now`,
 * which this module never called.
 *
 * Known limit: a fault whose delay keeps growing can still outrun this, since
 * each boot only has to beat the one before it. Every cycle is longer than the
 * last, so it degrades into slow reloads rather than a tight loop, and a fixed
 * ceiling would reopen the hole this closes.
 */
export function recoveryResetDelayMs(lastCrashUptime) {
    const previous = Number(lastCrashUptime) || 0;

    return Math.max(STABLE_UPTIME_MS, previous + STABLE_UPTIME_MS);
}

/**
 * Hands the recovery count back once this boot has outlived the last crash
 * *without crashing itself*.
 *
 * Both halves are load-bearing. The delay alone is not enough, because the
 * boot that spends the last of the budget is not reloaded: `recoverFromCrash`
 * suppresses the reload, `BoundaryFallback` renders, and this component stays
 * mounted above the boundary with its timer still running. Waiting it out on
 * the fallback screen would then restore the budget to a boot whose whole
 * evidence is that it crashed.
 */
export function useRecoveryReset() {
    React.useEffect(() => {
        let lastCrashUptime = 0;
        try {
            lastCrashUptime = Number(window.sessionStorage.getItem(LAST_CRASH_UPTIME_MARKER)) || 0;
        } catch (e) {
            // Storage unavailable, so there is no count to hand back either.
            return undefined;
        }

        const timer = setTimeout(() => {
            if (window[CRASHED_THIS_BOOT]) {
                // A suppressed crash leaves the fallback on screen without
                // reloading, so this timer outlives it and would otherwise hand
                // the budget back to a boot that demonstrably did not survive.
                return;
            }

            try {
                window.sessionStorage.removeItem(RECOVERY_MARKER);
                window.sessionStorage.removeItem(LAST_CRASH_UPTIME_MARKER);
            } catch (e) {
                // Nothing to clear if storage is unavailable.
            }
        }, recoveryResetDelayMs(lastCrashUptime));

        return () => clearTimeout(timer);
    }, []);
}
